/**
 * Pointer, touch, wheel and keyboard input for the stage and the domain editor.
 *
 * Stage, one pointer:
 *   brush / eraser — press inside the outlined tile to paint, anywhere else to travel
 *   travel         — drag to travel; double-click / double-tap recentres
 *   measuring      — tap to place points, drag a handle to move it, drag elsewhere to travel
 * Two pointers: translate + rotate about their midpoint. Wheel / trackpad scroll: travel.
 */
import type { Complex } from '../geometry/complex';
import { distance } from '../geometry/hyperbolic';
import { compose, inverse, rotation, translation } from '../geometry/mobius';
import { screenToDiskClamped } from '../render/camera';
import { arg } from '../geometry/complex';
import { apply } from '../geometry/mobius';
import type { App } from './app';
import { isMeasureTool, type Tool } from './state';

type Mode =
  | { kind: 'idle' }
  | { kind: 'paint'; id: number }
  | { kind: 'drag'; id: number; last: Complex; moved: boolean; startX: number; startY: number }
  | { kind: 'tap'; id: number; startX: number; startY: number; last: Complex }
  | { kind: 'handle'; id: number; index: number }
  | { kind: 'gesture' };

const TAP_SLOP = 6;

export function attachStageInput(app: App, el: HTMLCanvasElement): void {
  let mode: Mode = { kind: 'idle' };
  const pointers = new Map<number, { x: number; y: number }>();
  let gesturePrev: [Complex, Complex] | null = null;
  let lastTap = { t: 0, x: 0, y: 0 };

  const local = (e: PointerEvent | MouseEvent | WheelEvent) => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const tool = (): Tool => app.store.get().tool;
  const clampedDisk = (x: number, y: number) =>
    screenToDiskClamped(app.camera, app.displayModel, x, y);
  const widthOf = (e: PointerEvent) =>
    e.pointerType === 'pen' && e.pressure > 0 ? 0.25 + 0.75 * e.pressure : 1;

  const updateCursor = (x: number, y: number, inside: boolean) => {
    const t = tool();
    if (t === 'brush' || t === 'eraser') {
      const w = app.worldAt(x, y);
      const overTile = w !== null && app.inCentralTile(w);
      if (overTile || mode.kind === 'paint') {
        el.dataset.cursor = 'paint';
        app.setCursor({
          x,
          y,
          radius: app.brushRadiusAt(x, y),
          eraser: t === 'eraser',
          color: app.paintColor(),
        });
        return;
      }
      el.dataset.cursor = mode.kind === 'drag' ? 'grabbing' : 'grab';
      app.setCursor(null);
      return;
    }
    app.setCursor(null);
    if (t === 'travel') el.dataset.cursor = mode.kind === 'drag' ? 'grabbing' : 'grab';
    else el.dataset.cursor = inside ? 'crosshair' : '';
  };

  const startGesture = () => {
    const pts = [...pointers.values()];
    if (pts.length < 2) return;
    if (mode.kind === 'paint') app.painter.cancel();
    mode = { kind: 'gesture' };
    gesturePrev = [clampedDisk(pts[0].x, pts[0].y), clampedDisk(pts[1].x, pts[1].y)];
  };

  el.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    el.focus({ preventScroll: true });
    const p = local(e);
    pointers.set(e.pointerId, p);
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic events cannot be captured */
    }
    app.view.stop();
    if (app.penDown) app.setPen(false);
    if (pointers.size >= 2) {
      startGesture();
      return;
    }
    const t = tool();
    const world = app.worldAt(p.x, p.y);
    if (
      (t === 'brush' || t === 'eraser') &&
      world &&
      app.inCentralTile(world) &&
      !app.transitioning
    ) {
      app.stopDrift();
      mode = { kind: 'paint', id: e.pointerId };
      app.beginStroke(world, widthOf(e));
      updateCursor(p.x, p.y, true);
      return;
    }
    if (isMeasureTool(t) && !app.transitioning) {
      const h = app.handleAt(p.x, p.y);
      if (h >= 0) {
        mode = { kind: 'handle', id: e.pointerId, index: h };
        return;
      }
      if (world) {
        mode = {
          kind: 'tap',
          id: e.pointerId,
          startX: p.x,
          startY: p.y,
          last: clampedDisk(p.x, p.y),
        };
        return;
      }
    }
    app.stopDrift();
    mode = {
      kind: 'drag',
      id: e.pointerId,
      last: clampedDisk(p.x, p.y),
      moved: false,
      startX: p.x,
      startY: p.y,
    };
    updateCursor(p.x, p.y, !!world);
  });

  el.addEventListener('pointermove', (e) => {
    const p = local(e);
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, p);
    if (mode.kind === 'gesture') {
      const pts = [...pointers.values()];
      if (pts.length < 2 || !gesturePrev) return;
      const cur: [Complex, Complex] = [
        clampedDisk(pts[0].x, pts[0].y),
        clampedDisk(pts[1].x, pts[1].y),
      ];
      const [p0, p1] = gesturePrev;
      const [c0, c1] = cur;
      // midpoints along the geodesics, then the turn of the finger pair about the midpoint
      const mp = geodesicMidpoint(p0, p1);
      const mc = geodesicMidpoint(c0, c1);
      const tp = translation(mp);
      const tc = translation(mc);
      const dirP = arg(apply(inverse(tp), p1));
      const dirC = arg(apply(inverse(tc), c1));
      const g = compose(tc, compose(rotation(dirC - dirP), inverse(tp)));
      app.view.applyGesture(g, e.timeStamp);
      gesturePrev = cur;
      app.requestRender();
      return;
    }
    if (mode.kind === 'paint' && e.pointerId === mode.id) {
      const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e];
      for (const ce of events.length ? events : [e]) {
        const q = local(ce);
        const w = app.worldAt(q.x, q.y);
        if (w) app.extendStroke(w, widthOf(ce));
      }
      updateCursor(p.x, p.y, true);
      return;
    }
    if (mode.kind === 'drag' && e.pointerId === mode.id) {
      const z = clampedDisk(p.x, p.y);
      if (!mode.moved && Math.hypot(p.x - mode.startX, p.y - mode.startY) > 2) {
        mode.moved = true;
        app.markHintDone();
      }
      app.view.dragTo(mode.last, z, e.timeStamp);
      mode.last = z;
      app.requestRender();
      updateCursor(p.x, p.y, true);
      return;
    }
    if (mode.kind === 'tap' && e.pointerId === mode.id) {
      if (Math.hypot(p.x - mode.startX, p.y - mode.startY) > TAP_SLOP) {
        // it was a drag after all: travel
        app.stopDrift();
        const z = clampedDisk(p.x, p.y);
        app.view.dragTo(mode.last, z, e.timeStamp);
        mode = {
          kind: 'drag',
          id: mode.id,
          last: z,
          moved: true,
          startX: mode.startX,
          startY: mode.startY,
        };
        app.requestRender();
      }
      return;
    }
    if (mode.kind === 'handle' && e.pointerId === mode.id) {
      const w = app.worldAt(p.x, p.y);
      if (w) app.moveHandle(mode.index, w);
      return;
    }
    if (mode.kind === 'idle' && e.pointerType !== 'touch') {
      const w = app.worldAt(p.x, p.y);
      updateCursor(p.x, p.y, w !== null);
      if (isMeasureTool(tool()))
        el.dataset.cursor = app.handleAt(p.x, p.y) >= 0 ? 'grab' : 'crosshair';
    }
  });

  const finish = (e: PointerEvent) => {
    pointers.delete(e.pointerId);
    const p = local(e);
    if (mode.kind === 'gesture') {
      if (pointers.size < 2) {
        app.view.release(e.timeStamp);
        mode = { kind: 'idle' };
        gesturePrev = null;
      }
      return;
    }
    if (mode.kind === 'paint' && e.pointerId === mode.id) {
      app.endStroke();
      mode = { kind: 'idle' };
    } else if (mode.kind === 'drag' && e.pointerId === mode.id) {
      if (mode.moved) app.view.release(e.timeStamp);
      else maybeDoubleTap(p.x, p.y, e);
      mode = { kind: 'idle' };
    } else if (mode.kind === 'tap' && e.pointerId === mode.id) {
      const w = app.worldAt(p.x, p.y);
      if (w && e.type === 'pointerup') app.addMeasurePoint(w);
      mode = { kind: 'idle' };
    } else if (mode.kind === 'handle' && e.pointerId === mode.id) {
      mode = { kind: 'idle' };
    }
    if (e.pointerType !== 'touch') updateCursor(p.x, p.y, app.worldAt(p.x, p.y) !== null);
    else app.setCursor(null);
    app.requestRender();
  };

  const maybeDoubleTap = (x: number, y: number, e: PointerEvent) => {
    if (e.pointerType === 'mouse') return; // mice use dblclick
    const now = e.timeStamp;
    if (now - lastTap.t < 320 && Math.hypot(x - lastTap.x, y - lastTap.y) < 24) {
      app.recentreAt(x, y);
      lastTap = { t: 0, x: 0, y: 0 };
    } else lastTap = { t: now, x, y };
  };

  el.addEventListener('pointerup', finish);
  el.addEventListener('pointercancel', finish);
  el.addEventListener('pointerleave', (e) => {
    if (mode.kind === 'idle' && e.pointerType !== 'touch') {
      app.setCursor(null);
    }
  });

  el.addEventListener('dblclick', (e) => {
    const p = local(e);
    const t = tool();
    if (isMeasureTool(t)) return;
    const w = app.worldAt(p.x, p.y);
    if (!w) return;
    if ((t === 'brush' || t === 'eraser') && app.inCentralTile(w)) return;
    app.recentreAt(p.x, p.y);
  });

  el.addEventListener(
    'wheel',
    (e) => {
      if (e.ctrlKey) return; // leave browser zoom alone
      e.preventDefault();
      app.stopDrift();
      const scale = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
      const dx = e.deltaX * scale;
      const dy = e.deltaY * scale;
      const cx = app.camera.width / 2;
      const cy = app.camera.height / 2;
      const a = clampedDisk(cx, cy);
      const b = clampedDisk(cx - dx * 0.6, cy - dy * 0.6);
      if (distance(a, b) > 0) app.view.dragTo(a, b, e.timeStamp);
      app.markHintDone();
      app.requestRender();
    },
    { passive: false },
  );

  el.addEventListener('contextmenu', (e) => e.preventDefault());
}

function geodesicMidpoint(a: Complex, b: Complex): Complex {
  const ta = translation(a);
  const w = apply(inverse(ta), b);
  const r = Math.hypot(w.re, w.im);
  if (r === 0) return a;
  const d = Math.log((1 + r) / (1 - r));
  const rm = Math.tanh(d / 4);
  return apply(ta, { re: (w.re / r) * rm, im: (w.im / r) * rm });
}

/** The domain editor: painting at full size (any tool paints; the eraser erases). */
export function attachEditorInput(app: App, el: HTMLCanvasElement, cursorEl: HTMLElement): void {
  let active: number | null = null;
  const local = (e: PointerEvent) => {
    const r = el.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const showCursor = (x: number, y: number) => {
    const r = app.editorBrushRadius();
    cursorEl.hidden = false;
    cursorEl.style.transform = `translate(${x - r}px, ${y - r}px)`;
    cursorEl.style.width = `${2 * r}px`;
    cursorEl.style.height = `${2 * r}px`;
    cursorEl.dataset.eraser = String(app.store.get().tool === 'eraser');
    cursorEl.style.setProperty('--cursor-color', app.paintColor());
  };
  el.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (active !== null) return;
    active = e.pointerId;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic */
    }
    const p = local(e);
    app.stopDrift();
    const t = app.store.get().tool;
    if (t !== 'brush' && t !== 'eraser') app.store.set({ tool: 'brush' });
    app.beginStroke(
      app.editorWorldAt(p.x, p.y),
      e.pointerType === 'pen' && e.pressure > 0 ? 0.25 + 0.75 * e.pressure : 1,
    );
    showCursor(p.x, p.y);
  });
  el.addEventListener('pointermove', (e) => {
    const p = local(e);
    if (e.pointerType !== 'touch') showCursor(p.x, p.y);
    if (active !== e.pointerId) return;
    const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e];
    for (const ce of events.length ? events : [e]) {
      const q = local(ce);
      app.extendStroke(
        app.editorWorldAt(q.x, q.y),
        ce.pointerType === 'pen' && ce.pressure > 0 ? 0.25 + 0.75 * ce.pressure : 1,
      );
    }
  });
  const end = (e: PointerEvent) => {
    if (active !== e.pointerId) return;
    active = null;
    app.endStroke();
    if (e.pointerType === 'touch') cursorEl.hidden = true;
  };
  el.addEventListener('pointerup', end);
  el.addEventListener('pointercancel', end);
  el.addEventListener('pointerleave', () => {
    if (active === null) cursorEl.hidden = true;
  });
}

const MOVE_KEYS: Record<string, string> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowUp: 'up',
  ArrowDown: 'down',
  KeyA: 'left',
  KeyD: 'right',
  KeyW: 'up',
  KeyS: 'down',
  KeyQ: 'ccw',
  KeyE: 'cw',
};

/** Global keyboard shortcuts (ignored while typing in a form field or inside a dialog). */
export function attachKeyboard(
  app: App,
  stage: HTMLElement,
  actions: { escape: () => boolean; showKeys: () => void },
): void {
  const typing = (t: EventTarget | null) => {
    const el = t as HTMLElement | null;
    if (!el || !el.closest) return false;
    if (el.closest('dialog, .drawer')) return true;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || el.isContentEditable;
  };
  const onStage = (t: EventTarget | null) => t === stage || t === document.body || t === null;

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (actions.escape()) e.preventDefault();
      return;
    }
    if (typing(e.target)) return;
    const mod = e.ctrlKey || e.metaKey;
    if (mod && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      if (e.shiftKey) app.redo();
      else app.undo();
      return;
    }
    if (mod && (e.key === 'y' || e.key === 'Y')) {
      e.preventDefault();
      app.redo();
      return;
    }
    if (mod || e.altKey) return;
    const move = MOVE_KEYS[e.code];
    if (move) {
      // arrows on a focused button or link would otherwise scroll nothing; take them for travel
      e.preventDefault();
      app.stopDrift();
      app.view.keyDown(move);
      app.markHintDone();
      app.requestRender();
      return;
    }
    switch (e.key) {
      case 'Home':
        e.preventDefault();
        app.goHome();
        return;
      case 'b':
      case 'B':
        app.store.set({ tool: 'brush' });
        return;
      case 'x':
      case 'X':
        app.store.set({ tool: 'eraser' });
        return;
      case 'h':
      case 'H':
        app.store.set({ tool: 'travel' });
        return;
      case 'g':
      case 'G':
        app.store.set({ tool: 'geodesic' });
        return;
      case 't':
      case 'T':
        app.store.set({ tool: 'triangle' });
        return;
      case 'c':
      case 'C':
        app.store.set({ tool: 'circle' });
        return;
      case 'm':
      case 'M':
        app.nextModel();
        return;
      case '1':
      case '2':
      case '3':
      case '4':
        app.store.set({ role: (Number(e.key) - 1) as 0 | 1 | 2 | 3 });
        return;
      case '[':
        app.store.set({ size: Math.max(0, app.store.get().size - 8) });
        return;
      case ']':
        app.store.set({ size: Math.min(100, app.store.get().size + 8) });
        return;
      case '?':
        actions.showKeys();
        return;
    }
    if (!onStage(e.target)) return;
    const t = app.store.get().tool;
    if (e.key === ' ' && (t === 'brush' || t === 'eraser')) {
      e.preventDefault();
      app.setPen(!app.penDown);
      return;
    }
    if (e.key === 'Enter' && isMeasureTool(t)) {
      e.preventDefault();
      app.addMeasurePoint(app.view.centreWorld());
    }
  });

  window.addEventListener('keyup', (e) => {
    const move = MOVE_KEYS[e.code];
    if (move) {
      app.view.keyUp(move);
      app.requestRender();
    }
  });
  window.addEventListener('blur', () => app.view.clearKeys());
}
